/**
 * useLocationCheck Hook
 * Hook for geo-fencing and location verification
 */

import { useState, useCallback, useEffect, useRef } from "react";
import * as Location from "expo-location";
import { LocationStepConfig, LocationSubmission } from "../types";

interface UseLocationCheckReturn {
  /** Current user location */
  currentLocation: Location.LocationObject | null;
  /** Distance to target in meters */
  distanceToTarget: number | null;
  /** Whether user is within the target radius */
  isWithinRadius: boolean;
  /** Location permission status */
  hasPermission: boolean | null;
  /** Whether location is being tracked */
  isTracking: boolean;
  /** Loading state (getting permission or initial location) */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Request location permission */
  requestPermission: () => Promise<boolean>;
  /** Start tracking location */
  startTracking: () => Promise<void>;
  /** Stop tracking location */
  stopTracking: () => void;
  /** Get current location once */
  getCurrentLocation: () => Promise<Location.LocationObject | null>;
  /** Create submission data for completed location step */
  createSubmission: () => LocationSubmission | null;
}

interface UseLocationCheckOptions {
  /** Target location config from step */
  targetConfig: LocationStepConfig;
  /** Auto-start tracking on mount */
  autoStart?: boolean;
  /** Update interval in milliseconds */
  updateInterval?: number;
  /** Accuracy setting */
  accuracy?: Location.Accuracy;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @returns Distance in meters
 */
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Hook for location tracking and geo-fencing
 */
export const useLocationCheck = (
  options: UseLocationCheckOptions
): UseLocationCheckReturn => {
  const {
    targetConfig,
    autoStart = false,
    updateInterval = 3000, // 3 seconds
    accuracy = Location.Accuracy.High,
  } = options;

  // State
  const [currentLocation, setCurrentLocation] =
    useState<Location.LocationObject | null>(null);
  const [distanceToTarget, setDistanceToTarget] = useState<number | null>(null);
  const [isWithinRadius, setIsWithinRadius] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const locationSubscription = useRef<Location.LocationSubscription | null>(
    null
  );

  /**
   * Update distance and check if within radius
   */
  const updateDistance = useCallback(
    (location: Location.LocationObject) => {
      const distance = calculateDistance(
        location.coords.latitude,
        location.coords.longitude,
        targetConfig.lat,
        targetConfig.lng
      );

      setDistanceToTarget(Math.round(distance));
      setIsWithinRadius(distance <= targetConfig.radius_meters);
    },
    [targetConfig.lat, targetConfig.lng, targetConfig.radius_meters]
  );

  /**
   * Request location permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const { status: foregroundStatus } =
        await Location.requestForegroundPermissionsAsync();

      if (foregroundStatus !== "granted") {
        setHasPermission(false);
        setError("Locatie toestemming is vereist voor deze stap");
        return false;
      }

      setHasPermission(true);
      return true;
    } catch (err) {
      console.error("Error requesting location permission:", err);
      setError("Fout bij opvragen locatie toestemming");
      setHasPermission(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get current location once
   */
  const getCurrentLocation =
    useCallback(async (): Promise<Location.LocationObject | null> => {
      setIsLoading(true);
      setError(null);

      try {
        // Check permission first
        if (hasPermission === null) {
          const granted = await requestPermission();
          if (!granted) return null;
        } else if (hasPermission === false) {
          setError("Locatie toestemming is vereist");
          return null;
        }

        const location = await Location.getCurrentPositionAsync({
          accuracy,
        });

        setCurrentLocation(location);
        updateDistance(location);

        return location;
      } catch (err) {
        console.error("Error getting current location:", err);
        setError("Fout bij ophalen locatie");
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [hasPermission, accuracy, requestPermission, updateDistance]);

  /**
   * Start tracking location
   */
  const startTracking = useCallback(async (): Promise<void> => {
    if (isTracking) return;

    setIsLoading(true);
    setError(null);

    try {
      // Check permission first
      if (hasPermission === null) {
        const granted = await requestPermission();
        if (!granted) return;
      } else if (hasPermission === false) {
        setError("Locatie toestemming is vereist");
        return;
      }

      // Get initial location
      const initialLocation = await Location.getCurrentPositionAsync({
        accuracy,
      });
      setCurrentLocation(initialLocation);
      updateDistance(initialLocation);

      // Start watching position
      locationSubscription.current = await Location.watchPositionAsync(
        {
          accuracy,
          timeInterval: updateInterval,
          distanceInterval: 5, // Minimum 5 meters between updates
        },
        (location) => {
          setCurrentLocation(location);
          updateDistance(location);
        }
      );

      setIsTracking(true);
    } catch (err) {
      console.error("Error starting location tracking:", err);
      setError("Fout bij starten locatie tracking");
    } finally {
      setIsLoading(false);
    }
  }, [
    isTracking,
    hasPermission,
    accuracy,
    updateInterval,
    requestPermission,
    updateDistance,
  ]);

  /**
   * Stop tracking location
   */
  const stopTracking = useCallback((): void => {
    if (locationSubscription.current) {
      locationSubscription.current.remove();
      locationSubscription.current = null;
    }
    setIsTracking(false);
  }, []);

  /**
   * Create submission data for completing the step
   */
  const createSubmission = useCallback((): LocationSubmission | null => {
    if (!currentLocation) return null;

    return {
      lat: currentLocation.coords.latitude,
      lng: currentLocation.coords.longitude,
      accuracy: currentLocation.coords.accuracy || 0,
      distance_from_target: distanceToTarget || 0,
    };
  }, [currentLocation, distanceToTarget]);

  // Auto-start tracking on mount if enabled
  useEffect(() => {
    if (autoStart) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [autoStart, startTracking, stopTracking]);

  // Check initial permission status
  useEffect(() => {
    const checkPermission = async () => {
      const { status } = await Location.getForegroundPermissionsAsync();
      setHasPermission(status === "granted");
    };
    checkPermission();
  }, []);

  return {
    currentLocation,
    distanceToTarget,
    isWithinRadius,
    hasPermission,
    isTracking,
    isLoading,
    error,
    requestPermission,
    startTracking,
    stopTracking,
    getCurrentLocation,
    createSubmission,
  };
};

/**
 * Format distance for display
 */
export const formatDistance = (meters: number | null): string => {
  if (meters === null) return "Onbekend";

  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }

  return `${(meters / 1000).toFixed(1)}km`;
};

/**
 * Get direction emoji based on bearing
 */
export const getDirectionEmoji = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
): string => {
  const dLng = toLng - fromLng;
  const y = Math.sin(dLng) * Math.cos(toLat);
  const x =
    Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng);
  let bearing = (Math.atan2(y, x) * 180) / Math.PI;
  bearing = (bearing + 360) % 360;

  // Convert bearing to direction
  if (bearing >= 337.5 || bearing < 22.5) return "⬆️";
  if (bearing >= 22.5 && bearing < 67.5) return "↗️";
  if (bearing >= 67.5 && bearing < 112.5) return "➡️";
  if (bearing >= 112.5 && bearing < 157.5) return "↘️";
  if (bearing >= 157.5 && bearing < 202.5) return "⬇️";
  if (bearing >= 202.5 && bearing < 247.5) return "↙️";
  if (bearing >= 247.5 && bearing < 292.5) return "⬅️";
  return "↖️";
};

